import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../aai/useAsync';
import type {
  AaiRequest, AaiSkill, ControlEvent, IntakePersonProgress, OrbitOverview, OwnerConnection, OwnerOverview, PortfolioRecommendation,
  PortfolioRecommendationList,
} from '../../types/aai';
import { Action, ErrorNote, PageBody } from '../../components/aa';
import { askOrbitAction } from '../../components/AskOrbit';
import { OrbitRoomPanel } from '../orbit-room/OrbitRoomPanel';
import { completion, completionWord } from '../orbit-room/phases';
import { useRecordLinks } from '../../hooks/useRecordLinks';
import { ActionUpdateDrawer } from '../champion/ActionUpdateDrawer';
import { ACTION_KIND_LABEL, isOpenRespondable, shortName, sideOf, subjectOf } from '../champion/actionStatus';
import { RecordDrawer, isOpenFor, recTitle } from '../champion/Recommendation';
import { CardSkeleton, MiniBar, MiniRing, Reveal, SectionLabel, Skeleton, ageDays, plural } from './dashboard/primitives';
import { KpiStrip } from './dashboard/KpiStrip';
import type { Kpi } from './dashboard/KpiStrip';
import { Inbox } from './dashboard/Inbox';
import type { InboxItem, InboxKind } from './dashboard/Inbox';
import { ByDepartment, Pipeline, QuickWinsUse, RecentActivity, WeeklyFlow, stageOf, weeklySeries } from './dashboard/ProjectCharts';
import { CoverageCard, KnowledgeCard, MappedCard, TeamCard } from './dashboard/CompanyMap';

/*
 * The Owner's Home, as one dashboard: the headline numbers, the projects and
 * what waits on the Owner side by side, the company as Orbit reads it, then
 * adoption. The Orbit Room sits underneath as the place to answer cards; its
 * Overview is the company map above, so the Room keeps only its other tabs.
 *
 * Every number is read from the same endpoints the Projects, People and Room
 * screens use, and every mark opens the record behind it.
 */

const CONNECTION_STATUS: Record<string, string> = {
  connected: 'ready',
  pending: 'is pending',
  needs_reconnect: 'needs reconnecting',
  error: 'has an error',
  expired: 'has expired',
  disconnected: 'is disconnected',
};

const connectionTitle = (c: OwnerConnection): string => `${c.provider} ${CONNECTION_STATUS[c.status] || c.status}`;

const greeting = (): string => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

const firstName = (): string => {
  const name = (localStorage.getItem('user_name') || '').trim();
  if (!name || name.includes('@')) return '';
  return name.split(/\s+/)[0];
};

/* How each kind of decision is counted in words, for the KPI's foot line. */
const KIND_WORDS: Record<InboxKind, [string, string]> = {
  opportunity: ['opportunity', 'opportunities'],
  request: ['approval', 'approvals'],
  quote: ['budget', 'budgets'],
  action: ['action', 'actions'],
  recommendation: ['recommendation', 'recommendations'],
  connection: ['connection alert', 'connection alerts'],
};

const scrollToId = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

const DashboardSkeleton: React.FC = () => (
  <PageBody>
    <Skeleton className="mb-2 h-8 w-48" />
    <Skeleton className="mb-7 h-4 w-80" />
    <Skeleton className="h-[112px] w-full rounded-[14px]" />
    <div className="mt-4 grid gap-4 xl:grid-cols-12">
      <div className="xl:col-span-8"><CardSkeleton height={300} /></div>
      <div className="xl:col-span-4"><CardSkeleton height={300} /></div>
    </div>
  </PageBody>
);

const OwnerHome: React.FC = () => {
  const navigate = useNavigate();
  const clientId = localStorage.getItem('client_id') || '';
  const overview = useAsync<OwnerOverview>(() => aaiApi.getOwnerOverview(), 'Could not load your overview.');
  const room = useAsync<OrbitOverview>(() => aaiApi.getOrbitOverview(), 'Could not load the Room.');
  const blockers = useAsync<{ events: ControlEvent[] }>(() => aaiApi.listControlEvents(), 'Could not load control actions.');
  const recs = useAsync<PortfolioRecommendationList>(() => aaiApi.listPortfolioRecommendations(), 'Could not load portfolio recommendations.');
  const requests = useAsync<{ scope: string; requests: AaiRequest[] }>(() => aaiApi.listRequests(), 'Could not load your projects.');
  const skills = useAsync<{ skills: AaiSkill[]; can_manage: boolean }>(() => aaiApi.listSkills(), 'Could not load Quick Wins.');
  const intake = useAsync<{ people: Record<string, IntakePersonProgress> }>(() => aaiApi.intakePeople(clientId), 'Could not load intake progress.');
  const links = useRecordLinks();
  const setOpenOpportunity = links.opportunity;
  const setOpenProject = links.project;
  const [blocker, setBlocker] = useState<ControlEvent | null>(null);
  const [openRec, setOpenRec] = useState<PortfolioRecommendation | null>(null);
  const [department, setDepartment] = useState('');
  const me = localStorage.getItem('user_email') || '';
  const [params] = useSearchParams();
  const recParam = params.get('recommendation');

  useEffect(() => {
    if (!recParam || !recs.data) return;
    const found = recs.data.recommendations.find((r) => r.id === recParam);
    if (found) setOpenRec(found);
  }, [recParam, recs.data]);

  const allRequests = useMemo(() => (requests.data ? requests.data.requests : []), [requests.data]);
  const open = useMemo(() => allRequests.filter((r) => r.status !== 'declined'), [allRequests]);
  const weeks = useMemo(() => weeklySeries(allRequests), [allRequests]);

  // Only the first read shows a skeleton; a reload after a decision keeps the page.
  if (!overview.data && !overview.error) return <DashboardSkeleton />;
  if (!overview.data) return <PageBody><ErrorNote message={overview.error || 'Something went wrong.'} /></PageBody>;

  const data = overview.data;
  const routedToMe = (blockers.data?.events || []).filter((e) => isOpenRespondable(e) && sideOf(e, me) === 'recipient');
  const recommendations = (recs.data?.recommendations || []).filter((r) => isOpenFor(r, me));

  const reloadAll = () => {
    overview.reload();
    requests.reload();
    room.reload();
    intake.reload();
  };
  const openSettings = () => window.dispatchEvent(new CustomEvent('settings:open', { detail: { section: 'connectors' } }));
  const answerCards = () => {
    window.dispatchEvent(new CustomEvent('orbit-room:tab', { detail: { tab: 'journey' } }));
    window.setTimeout(() => scrollToId('orbit-room'), 60);
  };
  const pickDepartment = (name: string) => {
    setDepartment(name);
    if (name) document.getElementById('owner-pipeline')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const inbox: InboxItem[] = [
    ...data.decisions.map((d) => ({
      key: `${d.kind}:${d.id}`,
      kind: d.kind,
      title: d.title,
      meta: d.department || 'Whole company',
      since: d.since,
      onOpen: () => {
        if (d.kind === 'opportunity') {
          setOpenOpportunity(d.id);
          return;
        }
        if (d.request_id) setOpenProject(d.request_id);
      },
    })),
    ...routedToMe.map((e) => ({
      key: `action:${e.id}`,
      kind: 'action' as const,
      title: subjectOf(e),
      meta: `${ACTION_KIND_LABEL[e.kind] || e.kind} from ${shortName(e.actor_email)}`,
      since: e.created_at,
      onOpen: () => setBlocker(e),
    })),
    ...recommendations.map((rec) => ({
      key: `rec:${rec.id}`,
      kind: 'recommendation' as const,
      title: recTitle(rec),
      meta: `From ${shortName(rec.sent_by)} · ${plural(rec.items.length, 'priority', 'priorities')}`,
      since: rec.sent_at,
      onOpen: () => setOpenRec(rec),
    })),
    ...data.connections_attention.map((c) => ({
      key: `conn:${c.provider}`,
      kind: 'connection' as const,
      title: connectionTitle(c),
      meta: c.owner_email ? `Connection owner ${c.owner_email}` : 'Review access in Settings',
      since: c.updated_at || c.last_sync_at,
      onOpen: openSettings,
    })),
  ];
  const inboxLoading = blockers.loading || recs.loading;
  const oldest = inbox.reduce((max, i) => Math.max(max, ageDays(i.since)), 0);

  let intakeProgress: Record<string, IntakePersonProgress> | null | undefined = null;
  if (intake.data) intakeProgress = intake.data.people;
  if (intake.error) intakeProgress = undefined;
  const awaitingApproval = open.filter((r) => ['asked', 'review'].includes(stageOf(r))).length;
  const delivered = open.filter((r) => stageOf(r) === 'live').length;
  const team = room.data ? room.data.counts : null;
  const mapped = room.data ? completion(room.data) : null;

  const kindCounts = (Object.keys(KIND_WORDS) as InboxKind[])
    .map((k) => ({ k, n: inbox.filter((i) => i.kind === k).length }))
    .filter((x) => x.n > 0)
    .sort((a, b) => b.n - a.n);
  let waitingFoot = 'Nothing needs you right now';
  if (kindCounts.length > 0) waitingFoot = kindCounts.slice(0, 2).map((x) => plural(x.n, KIND_WORDS[x.k][0], KIND_WORDS[x.k][1])).join(' · ');
  const inFlight = open.length - delivered;
  const inDelivery = inFlight - awaitingApproval;
  let flightFoot = 'Counting your projects';
  if (requests.error) flightFoot = 'Could not read your projects';
  if (requests.data) flightFoot = `${awaitingApproval} awaiting approval · ${inDelivery} in delivery`;
  let winsFoot = 'Ready for your team';
  if (requests.data) winsFoot = `${plural(delivered, 'project', 'projects')} delivered`;
  let teamFoot = 'Counting your people';
  if (room.error) teamFoot = 'Could not count your people';
  if (team && team.people === 0) teamFoot = 'Nobody added yet';
  if (team && team.people > 0) teamFoot = team.onboarded >= team.people ? 'Everyone is in' : `${team.people - team.onboarded} still to onboard`;
  let mappedFoot = 'Reading the Room';
  if (room.error) mappedFoot = 'Could not read the Room';
  if (room.data && mapped !== null) mappedFoot = completionWord(mapped, (room.data.thin || []).length);

  const kpis: Kpi[] = [
    {
      key: 'waiting',
      label: 'Needs your decision',
      value: inbox.length,
      attention: inbox.length > 0,
      foot: waitingFoot,
      visual: oldest > 0 ? (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums ${oldest >= 30 ? 'bg-accent-100 text-ink ring-1 ring-accent-300' : 'bg-canvas-inset text-ink-muted'}`} title="How long the oldest one has waited">
          Oldest {oldest}d
        </span>
      ) : undefined,
      onOpen: () => scrollToId('owner-decisions'),
    },
    {
      key: 'flight',
      label: 'Projects in flight',
      value: requests.data ? inFlight : null,
      failed: Boolean(requests.error),
      foot: flightFoot,
      visual: requests.data && inFlight > 0 ? (
        <MiniBar
          parts={[
            { value: awaitingApproval, tone: '#D7D8D6', label: 'Awaiting approval' },
            { value: inDelivery, tone: '#303238', label: 'In delivery' },
          ]}
        />
      ) : undefined,
      onOpen: () => scrollToId('owner-pipeline'),
    },
    {
      key: 'wins',
      label: 'Quick Wins live',
      value: data.counts.quick_wins,
      foot: winsFoot,
      onOpen: () => navigate('/projects?tab=quick_wins'),
    },
    {
      key: 'team',
      label: 'Team onboarded',
      value: team ? team.onboarded : null,
      failed: Boolean(room.error),
      suffix: team ? `/${team.people}` : undefined,
      foot: teamFoot,
      visual: team ? <MiniRing value={team.people > 0 ? team.onboarded / team.people : 0} label={`${team.onboarded} of ${team.people} onboarded`} tone="#18191D" /> : undefined,
      onOpen: () => navigate('/team'),
    },
    {
      key: 'mapped',
      label: 'Company mapped',
      value: mapped,
      failed: Boolean(room.error),
      suffix: '%',
      foot: mappedFoot,
      visual: mapped !== null ? <MiniRing value={mapped / 100} label={`${mapped}% of the company mapped`} /> : undefined,
      onOpen: () => scrollToId('owner-company-map'),
    },
  ];

  const name = firstName();
  const company = localStorage.getItem('client_name') || '';
  const today = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <PageBody>
      <Reveal className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0 flex-1 basis-[260px]">
          <p className="m-0 mb-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">{today}</p>
          <h1 className="m-0 text-[26px] font-bold leading-[33px] tracking-[-0.015em] text-ink sm:text-[30px] sm:leading-[38px]">Dashboard</h1>
          <p className="m-0 mt-1.5 text-[14px] text-ink-muted">
            {greeting()}{name ? `, ${name}` : ''}. {company ? `Here is ${company} at a glance.` : 'Here is your company at a glance.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Action variant="quiet" to="/projects" icon={<ArrowRight size={14} />}>Projects</Action>
          {askOrbitAction()}
        </div>
      </Reveal>

      <KpiStrip items={kpis} />

      <div className="mt-4 grid gap-4 xl:grid-cols-12">
        {/* Below xl both columns dissolve into one list so the inbox can lead. */}
        <div className="contents xl:col-span-8 xl:flex xl:min-w-0 xl:flex-col xl:gap-4">
          <Reveal order={2} className="order-2 xl:order-none">
            {requests.data && (
              <Pipeline
                requests={open}
                department={department}
                onClearDepartment={() => setDepartment('')}
                onOpenProject={setOpenProject}
                onAllProjects={() => navigate('/projects')}
              />
            )}
            {!requests.data && !requests.error && <CardSkeleton height={250} />}
            {requests.error && <ErrorNote message={requests.error} />}
          </Reveal>
          <div className="order-3 grid flex-1 gap-4 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] xl:order-none">
            <Reveal order={5}>
              {requests.data && <WeeklyFlow weeks={weeks} />}
              {!requests.data && <CardSkeleton height={260} />}
            </Reveal>
            <Reveal order={6}>
              {requests.data && <ByDepartment requests={open} selected={department} onSelect={pickDepartment} />}
              {!requests.data && <CardSkeleton height={260} />}
            </Reveal>
          </div>
        </div>
        {/* On wide screens this column takes the left column's height and the
            activity list scrolls, so a long inbox never stretches the charts. */}
        <div className="contents xl:col-span-4 xl:flex xl:h-0 xl:min-h-full xl:min-w-0 xl:flex-col xl:gap-4">
          <Reveal order={3} className="order-1 xl:order-none xl:shrink-0">
            <Inbox items={inbox} loading={inboxLoading} />
          </Reveal>
          <Reveal order={4} className="order-4 min-h-[260px] xl:order-none xl:min-h-0 xl:flex-1">
            {requests.data && <RecentActivity requests={allRequests} onOpenProject={setOpenProject} />}
            {!requests.data && <CardSkeleton height={240} />}
          </Reveal>
        </div>
      </div>

      <div id="owner-company-map" className="mt-8 scroll-mt-24">
        <SectionLabel title="Company map" sub="What Orbit understands about the company, area by area" />
        {room.error && <ErrorNote message={room.error} />}
        {!room.data && !room.error && (
          <div className="grid gap-4 xl:grid-cols-12">
            <div className="xl:col-span-3"><CardSkeleton height={280} /></div>
            <div className="xl:col-span-6"><CardSkeleton height={280} /></div>
            <div className="xl:col-span-3"><CardSkeleton height={280} /></div>
          </div>
        )}
        {room.data && (
          <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-12">
            <Reveal order={6} className="xl:col-span-3"><MappedCard room={room.data} onAnswer={answerCards} /></Reveal>
            <Reveal order={7} className="lg:order-last lg:col-span-2 xl:order-none xl:col-span-6"><CoverageCard room={room.data} /></Reveal>
            <Reveal order={8} className="xl:col-span-3"><KnowledgeCard room={room.data} onAnswer={answerCards} /></Reveal>
          </div>
        )}
      </div>

      <div className="mt-8">
        <SectionLabel title="Adoption" sub="What the team uses, and who is in" />
        <div className="grid gap-4 lg:grid-cols-2">
          <Reveal order={9}>
            {skills.data && <QuickWinsUse skills={skills.data.skills} liveTotal={data.counts.quick_wins} onSeeAll={() => navigate('/projects?tab=quick_wins')} />}
            {!skills.data && <CardSkeleton height={260} />}
          </Reveal>
          <Reveal order={10}>
            {room.data && (
              <TeamCard
                room={room.data}
                intake={intakeProgress}
                onOpen={() => navigate('/team')}
              />
            )}
            {!room.data && <CardSkeleton height={260} />}
          </Reveal>
        </div>
      </div>

      {clientId && (
        <div id="orbit-room" className="mt-8 scroll-mt-24">
          <OrbitRoomPanel viewer="owner" clientId={clientId} hideOverview onChanged={reloadAll} />
        </div>
      )}

      {blocker && <ActionUpdateDrawer event={blocker} onUpdated={blockers.reload} onClose={() => setBlocker(null)} />}
      {openRec && (
        <RecordDrawer
          rec={openRec}
          me={me}
          onChanged={() => recs.reload()}
          onOpenProject={(id) => { setOpenRec(null); setOpenProject(id); }}
          onClose={() => setOpenRec(null)}
        />
      )}
    </PageBody>
  );
};

export default OwnerHome;
